
import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { FeaturedTrack } from '@/types/featuredTracks';
import { toast } from '@/components/ui/use-toast';
import { 
  addFeaturedTrack as addTrack,
  deleteFeaturedTrack as deleteTrack,
  reorderFeaturedTracks as reorderTracks
} from '@/services/featuredTracksService';

export function useTrackOperations(refreshTracks: () => Promise<void>) {
  const addFeaturedTrack = async (trackUrl: string) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        toast({
          title: 'Authentication Error',
          description: 'You need to be logged in to add tracks',
          variant: 'destructive',
        });
        return false;
      }

      await addTrack(trackUrl, session);
      toast({
        title: 'Track added',
        description: 'Featured track has been added successfully',
      });
      refreshTracks();
      return true;
    } catch (error: any) {
      console.error('Error adding track:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to add track',
        variant: 'destructive',
      });
      return false;
    }
  };

  const deleteTrackById = async (trackId: string) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        toast({
          title: 'Authentication Error',
          description: 'You need to be logged in to delete tracks',
          variant: 'destructive',
        });
        return;
      }
      
      console.log('Deleting track:', trackId);
      
      // Call the service to delete the track
      const response = await deleteTrack(trackId, session);
      
      if (response) {
        toast({
          title: 'Track removed',
          description: 'Featured track has been removed successfully',
        });
        // Refresh the tracks list to show the updated list
        refreshTracks();
      }
    } catch (error: any) {
      console.error('Error deleting track:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to remove track',
        variant: 'destructive',
      });
    }
  };

  const reorderTracksList = async (updatedTracks: FeaturedTrack[]) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        toast({
          title: 'Authentication Error',
          description: 'You need to be logged in to reorder tracks',
          variant: 'destructive',
        });
        refreshTracks();
        return;
      }
      
      await reorderTracks(updatedTracks, session);
    } catch (error: any) {
      console.error('Error updating track order:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update track order',
        variant: 'destructive',
      });
      refreshTracks();
    }
  };

  return {
    addFeaturedTrack,
    deleteTrack: deleteTrackById,
    reorderTracks: reorderTracksList
  };
}
